import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Loader2, Download, ThumbsUp, ThumbsDown, AlertTriangle, Layers3, FileWarning, Repeat2, MessageSquare, Gauge, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const normKey = (t) => {
  const toks = [...new Set(((t || "").toLowerCase().match(/[a-z0-9]+/g) || []).filter((w) => w.length > 2))].sort();
  return toks.join(" ") || (t || "").trim().toLowerCase();
};

function Stat({ icon: Icon, label, value, color = "text-primary", testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <Icon className={`w-4 h-4 ${color} mb-2`} />
      <div className="text-2xl font-heading font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function Row({ d, evalKeys, onAddEval }) {
  const [open, setOpen] = useState(false);
  const fb = d.feedback?.rating;
  return (
    <>
      <TableRow data-testid={`log-row-${d.log_id}`} className="cursor-pointer" onClick={() => setOpen((o) => !o)}>
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(d.created_at)}</TableCell>
        <TableCell><Badge variant="outline" className="capitalize">{d.role === "shop" ? "Partner" : d.role}</Badge></TableCell>
        <TableCell className="max-w-[320px] truncate">{d.question}</TableCell>
        <TableCell>
          {fb === "up" && <ThumbsUp className="w-4 h-4 text-emerald-400" />}
          {fb === "down" && <ThumbsDown className="w-4 h-4 text-rose-400" />}
          {!fb && <span className="text-muted-foreground text-xs">—</span>}
        </TableCell>
        <TableCell>
          {d.low_confidence ? <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30">low conf</Badge> : <span className="text-xs text-muted-foreground">{(d.top_score ?? 0).toFixed(2)}</span>}
          {d.uncertain && <Badge className="ml-1 bg-rose-500/15 text-rose-400 border border-rose-500/30">uncertain</Badge>}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow data-testid={`log-detail-${d.log_id}`}>
          <TableCell colSpan={5} className="bg-background/60">
            <div className="text-xs text-muted-foreground mb-1">{d.email} · top score {(d.top_score ?? 0).toFixed(2)} · {d.retrieved_count ?? 0} docs retrieved</div>
            <div className="text-sm whitespace-pre-wrap mb-2"><span className="font-semibold">Q:</span> {d.question}</div>
            <div className="text-sm whitespace-pre-wrap mb-2"><span className="font-semibold">A:</span> {d.answer}</div>
            <div className="flex flex-wrap gap-1">
              {(d.sources || []).map((s, j) => (
                <span key={j} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] border border-primary/20" title={s.path}>{s.title}{s.score != null ? ` · ${s.score.toFixed(2)}` : ""}</span>
              ))}
            </div>
            <div className="mt-3">
              {evalKeys?.has(normKey(d.question)) ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`eval-added-${d.log_id}`}><Check className="w-3.5 h-3.5" /> In eval set</span>
              ) : (
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onAddEval?.(d.question, d.log_id, "log"); }} data-testid={`add-eval-${d.log_id}`}><Plus className="w-4 h-4 mr-1" />Add to Eval Set</Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AssistantLogs({ onBack }) {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evalKeys, setEvalKeys] = useState(new Set());
  const [filters, setFilters] = useState({ rating: "", role: "", q: "", date_from: "", date_to: "", low_confidence: false });

  const loadAnalytics = useCallback(async () => {
    try { const { data } = await api.get("/assistant/analytics"); setAnalytics(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  const loadEval = useCallback(async () => {
    try { const { data } = await api.get("/assistant/eval-cases"); setEvalKeys(new Set(data.norm_keys || [])); } catch { /* ignore */ }
  }, []);

  const addToEval = useCallback(async (question, logId, origin) => {
    try {
      const { data } = await api.post("/assistant/eval-cases", { question, log_id: logId || null, origin });
      if (data.added) { toast.success(`Added to eval set as ${data.id} → ${data.expected_primary_document || "?"}`); }
      else if (data.duplicate) { toast.info(`Already in eval set (${data.id})`); }
      setEvalKeys((s) => { const n = new Set(s); if (data.norm_key) n.add(data.norm_key); return n; });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  const loadLogs = useCallback(async (override) => {
    setLoading(true);
    const f = { ...filters, ...(override || {}) };
    const params = {};
    if (f.rating) params.rating = f.rating;
    if (f.role) params.role = f.role;
    if (f.q) params.q = f.q;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    if (f.low_confidence) params.low_confidence = true;
    try { const { data } = await api.get("/assistant/logs", { params }); setLogs(data.logs || []); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadAnalytics(); loadEval(); loadLogs(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    const rows = [["timestamp", "role", "email", "question", "answer", "rating", "top_score", "low_confidence", "uncertain", "sources"]];
    logs.forEach((d) => rows.push([
      d.created_at, d.role, d.email, d.question, (d.answer || "").replace(/\n/g, " "),
      d.feedback?.rating || "", (d.top_score ?? 0).toFixed(2), d.low_confidence ? "yes" : "no", d.uncertain ? "yes" : "no",
      (d.sources || []).filter((s) => !s.pinned).map((s) => s.title).join(" | "),
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = href; a.download = "assistant-logs.csv"; a.click(); URL.revokeObjectURL(href);
  };

  const s = analytics?.stats;
  const inputCls = "rounded-lg bg-background border border-border px-3 py-1.5 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-5" data-testid="assistant-logs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onBack} data-testid="logs-back"><ArrowLeft className="w-4 h-4 mr-1" />Back to Assistant</Button>
          <h2 className="font-heading font-bold text-lg">Assistant Logs & Insights</h2>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid="logs-export"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat icon={MessageSquare} label="Questions" value={s.total} testid="stat-total" />
          <Stat icon={ThumbsUp} label="Thumbs up" value={s.up} color="text-emerald-400" testid="stat-up" />
          <Stat icon={ThumbsDown} label="Thumbs down" value={s.down} color="text-rose-400" testid="stat-down" />
          <Stat icon={Gauge} label="Down rate" value={`${Math.round((s.down_rate || 0) * 100)}%`} color="text-rose-400" testid="stat-down-rate" />
          <Stat icon={AlertTriangle} label="Low confidence" value={s.low_confidence} color="text-amber-400" testid="stat-lowconf" />
          <Stat icon={FileWarning} label="Uncertain" value={s.uncertain} color="text-amber-400" testid="stat-uncertain" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[["overview", "Overview", Layers3], ["repeated", "Repeated questions", Repeat2], ["down", "Thumbs-down queue", ThumbsDown], ["all", "All logs", MessageSquare]].map(([k, l, Icon]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => { setTab(k); if (k === "down") { const f = { rating: "down", role: "", q: "", date_from: "", date_to: "", low_confidence: false }; setFilters(f); loadLogs(f); } else if (k === "all") { loadLogs(); } }} data-testid={`logs-tab-${k}`}>
            <Icon className="w-4 h-4 mr-1" />{l}
          </Button>
        ))}
      </div>

      {tab === "overview" && analytics && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="overview-repeated">
            <div className="font-semibold mb-1 flex items-center gap-2"><Repeat2 className="w-4 h-4 text-primary" />Top repeated questions <span className="text-xs text-muted-foreground font-normal">— primary beta signal</span></div>
            <div className="text-xs text-muted-foreground mb-3">Frequently asked or rephrased questions. Turn the important ones into permanent eval cases with one click.</div>
            <Table><TableHeader><TableRow><TableHead>Question</TableHead><TableHead className="text-right">Asked</TableHead><TableHead className="text-right">👍</TableHead><TableHead className="text-right">👎</TableHead><TableHead className="text-right">Low conf</TableHead><TableHead>Eval</TableHead></TableRow></TableHeader>
              <TableBody>{(analytics.repeated || []).slice(0, 6).map((g, i) => (
                <TableRow key={i} data-testid={`overview-repeated-${i}`}>
                  <TableCell className="max-w-[360px] truncate">{g.question}</TableCell>
                  <TableCell className="text-right font-semibold">{g.count}</TableCell>
                  <TableCell className="text-right text-emerald-400">{g.up || 0}</TableCell>
                  <TableCell className="text-right text-rose-400">{g.down || 0}</TableCell>
                  <TableCell className="text-right text-amber-400">{g.low_confidence || 0}</TableCell>
                  <TableCell>{evalKeys.has(normKey(g.question)) ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" />Added</span> : <Button size="sm" variant="outline" onClick={() => addToEval(g.question, null, "repeated")} data-testid={`add-eval-overview-${i}`}><Plus className="w-4 h-4 mr-1" />Add</Button>}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="font-semibold mb-3 flex items-center gap-2"><Layers3 className="w-4 h-4 text-primary" />Most retrieved documents</div>
            <Table><TableHeader><TableRow><TableHead>Document</TableHead><TableHead className="text-right">Retrieved</TableHead><TableHead className="text-right">Down</TableHead></TableRow></TableHeader>
              <TableBody>{(analytics.source_usage || []).slice(0, 12).map((x) => (
                <TableRow key={x.path} data-testid={`source-usage-${x.path}`}><TableCell className="text-sm">{x.title}</TableCell><TableCell className="text-right">{x.retrieved}</TableCell><TableCell className="text-right">{x.down > 0 ? <span className="text-rose-400">{x.down}</span> : 0}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="font-semibold mb-3 flex items-center gap-2"><FileWarning className="w-4 h-4 text-amber-400" />Never retrieved ({analytics.never_retrieved?.length || 0})</div>
            <div className="text-xs text-muted-foreground mb-2">Docs no question has matched yet — candidates for better aliases or removal.</div>
            <div className="flex flex-wrap gap-1 max-h-[280px] overflow-y-auto">
              {(analytics.never_retrieved || []).map((x) => (<span key={x.path} className="inline-flex rounded-full bg-background border border-border px-2 py-0.5 text-[11px]" title={x.path}>{x.title}</span>))}
              {(analytics.never_retrieved || []).length === 0 && <span className="text-xs text-muted-foreground">Every document has been retrieved at least once.</span>}
            </div>
          </div>
        </div>
        </div>
      )}

      {(tab === "all" || tab === "down") && (
        <div className="space-y-3">
          {tab === "all" && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3" data-testid="logs-filters">
              <select className={inputCls} value={filters.rating} onChange={(e) => setFilters({ ...filters, rating: e.target.value })} data-testid="filter-rating"><option value="">Any rating</option><option value="up">👍 Up</option><option value="down">👎 Down</option><option value="none">No feedback</option></select>
              <select className={inputCls} value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="filter-role"><option value="">Any role</option><option value="admin">Admin</option><option value="shop">Partner</option></select>
              <input className={inputCls} placeholder="Search questions…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-q" />
              <input type="date" className={inputCls} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-from" />
              <input type="date" className={inputCls} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-to" />
              <label className="flex items-center gap-1 text-sm text-muted-foreground"><input type="checkbox" checked={filters.low_confidence} onChange={(e) => setFilters({ ...filters, low_confidence: e.target.checked })} data-testid="filter-lowconf" />Low confidence only</label>
              <Button size="sm" onClick={() => loadLogs()} data-testid="filter-apply">Apply</Button>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            {loading ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
              <Table>
                <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Role</TableHead><TableHead>Question</TableHead><TableHead>Feedback</TableHead><TableHead>Confidence</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No logs match.</TableCell></TableRow>}
                  {logs.map((d) => <Row key={d.log_id} d={d} evalKeys={evalKeys} onAddEval={addToEval} />)}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="text-xs text-muted-foreground">Showing {logs.length} log(s). Click a row to expand the full answer and sources.</div>
        </div>
      )}

      {tab === "repeated" && analytics && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Question (representative)</TableHead><TableHead className="text-right">Asked</TableHead><TableHead className="text-right">👍</TableHead><TableHead className="text-right">👎</TableHead><TableHead className="text-right">Low conf</TableHead><TableHead>Last asked</TableHead><TableHead>Eval</TableHead></TableRow></TableHeader>
            <TableBody>
              {(analytics.repeated || []).map((g, i) => (
                <TableRow key={i} data-testid={`repeated-row-${i}`}>
                  <TableCell className="max-w-[360px] truncate">{g.question}</TableCell>
                  <TableCell className="text-right font-semibold">{g.count}</TableCell>
                  <TableCell className="text-right text-emerald-400">{g.up || 0}</TableCell>
                  <TableCell className="text-right text-rose-400">{g.down || 0}</TableCell>
                  <TableCell className="text-right text-amber-400">{g.low_confidence || 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(g.last_asked)}</TableCell>
                  <TableCell>{evalKeys.has(normKey(g.question)) ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" />Added</span> : <Button size="sm" variant="outline" onClick={() => addToEval(g.question, null, "repeated")} data-testid={`add-eval-repeated-${i}`}><Plus className="w-4 h-4 mr-1" />Add</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
